/** Persist last-known Find peer rows per user + group for instant re-open. */

import { FIND_PEER_FALLBACK_LABEL, sanitizeSnapshotDisplayName } from './findPeerDisplayName';
import { bleNearbyLabel, formatFindPeerDistanceWithWalkTime } from './runtime';

function storageKey(userId, groupId) {
  return `prii-find-peers-${userId}-${groupId}`;
}

/** Serializable subset of useProximity peer rows (enough for radar + list). */
export function serializeFindPeerRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  return rows.map((row) => ({
    peerId: row.peerId,
    // Fall back here, not only in hydrateSnapshotRow. sanitize returns null for
    // blank / UUID-shaped names, and a null displayName is not assignable to the
    // radar's row type -- serialized rows feed it directly. Hydrate already
    // applies this same label, so the rendered result is unchanged.
    displayName: sanitizeSnapshotDisplayName(row.displayName) ?? FIND_PEER_FALLBACK_LABEL,
    photoUrl: row.photoUrl ?? null,
    bearingDeg: row.bearingDeg ?? null,
    uwbActive: row.uwbActive === true,
    uwbDistance:
      row.uwbDistance != null && Number.isFinite(row.uwbDistance) ? row.uwbDistance : null,
    uwbDirectionAvailable: row.uwbDirectionAvailable === true,
    display: {
      kind: row.display?.kind ?? 'none',
      distanceMeters:
        row.display?.distanceMeters != null && Number.isFinite(row.display.distanceMeters)
          ? row.display.distanceMeters
          : null,
      listPreview: row.display?.listPreview ?? null,
      sortTier: row.display?.sortTier ?? 99,
      sortDistance:
        row.display?.sortDistance != null && Number.isFinite(row.display.sortDistance)
          ? row.display.sortDistance
          : 999,
      gpsLastKnownAt:
        row.display?.gpsLastKnownAt != null && Number.isFinite(row.display.gpsLastKnownAt)
          ? row.display.gpsLastKnownAt
          : null,
      horizontalAccuracyMeters:
        row.display?.horizontalAccuracyMeters != null &&
        Number.isFinite(row.display.horizontalAccuracyMeters)
          ? row.display.horizontalAccuracyMeters
          : null,
    },
  }));
}

function hydrateListPreview(row) {
  const kind = row.display?.kind;
  const distanceMeters = row.display?.distanceMeters;
  if (kind === 'ble-in-range') {
    return bleNearbyLabel(distanceMeters);
  }
  if (distanceMeters == null || !Number.isFinite(distanceMeters)) {
    return row.display?.listPreview ?? null;
  }
  return formatFindPeerDistanceWithWalkTime(distanceMeters, {
    peerId: row.peerId,
    uwbPrecise: kind === 'uwb-live',
    horizontalAccuracyMeters: row.display?.horizontalAccuracyMeters ?? null,
  });
}

function hydrateSnapshotRow(row) {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const displayName = sanitizeSnapshotDisplayName(row.displayName) ?? FIND_PEER_FALLBACK_LABEL;
  const display = row.display
    ? { ...row.display, listPreview: hydrateListPreview(row) }
    : row.display;
  return {
    ...row,
    displayName,
    photoUrl: row.photoUrl ?? null,
    display,
  };
}

export function readFindPeerSnapshot(userId, groupId) {
  if (!userId || !groupId || typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = localStorage.getItem(storageKey(userId, groupId));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(hydrateSnapshotRow).filter(Boolean);
  } catch {
    return [];
  }
}

export function writeFindPeerSnapshot(userId, groupId, rows) {
  if (!userId || !groupId || typeof window === 'undefined') {
    return;
  }
  const key = storageKey(userId, groupId);
  try {
    const serialized = serializeFindPeerRows(rows);
    if (serialized.length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(serialized));
  } catch {
    // ignore quota / private mode
  }
}
